import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToId } from "./Header";
import { useLang } from "@/i18n/LanguageContext";

export const Footer = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const COLS = [
    {
      title: t("footer.col1"),
      links: [
        { label: t("nav.about"), href: "#about" },
        { label: t("nav.mission"), href: "#mission" },
        { label: t("nav.whatWeDo"), href: "#what-we-do" },
      ],
    },
    {
      title: t("footer.col2"),
      links: [
        { label: t("nav.roadmap"), href: "#roadmap" },
        { label: t("nav.nqm"), href: "#nqm" },
        { label: t("nav.news"), href: "#news" },
        { label: t("footer.disclaimerLabel"), href: "#legal" },
        { label: t("nav.contact"), href: "#contact" },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div>
              <img
                src="/qsafe-logo.png"
                alt="QSafe India — Trust for the Quantum Era"
                className="h-14 w-auto rounded-lg shadow-[0_10px_24px_-10px_rgba(15,23,42,0.45)]"
              />
              <p className="mt-1.5 font-mono text-[9px] font-bold tracking-[0.32em] uppercase text-slate-500">
                Quantum Safe India
              </p>
            </div>
            <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-[0.26em] text-amber-600">
              {t("footer.tagline")}
            </p>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-sm">{t("footer.body")}</p>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{col.title}</p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`footer-link-${l.href.slice(1)}`}
                      onClick={(e) => { e.preventDefault(); scrollToId(l.href); }}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-700 transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div data-testid="footer-disclaimer" className="mt-12 pt-7 border-t border-slate-200">
          <button
            data-testid="footer-disclaimer-toggle"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className={`inline-flex items-center gap-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
              open
                ? "bg-blue-700 text-white border-blue-700 shadow-[0_10px_24px_-10px_rgba(29,78,216,0.55)]"
                : "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 hover:border-blue-500 hover:-translate-y-0.5"
            }`}
          >
            {t("footer.disclaimerLabel")}
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <p className="mt-2.5 text-xs text-slate-400 leading-relaxed max-w-5xl">
            {t("footer.disclaimer")}
          </p>
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-5 space-y-4 max-w-5xl">
                  {t("disclaimer.clauses").map((c, i) => (
                    <div key={i} className="flex gap-3.5">
                      <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full bg-blue-50 border border-blue-200 font-mono text-[10px] font-extrabold text-blue-700">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-xs font-bold text-slate-600">{c.title}</p>
                        <p className="mt-1 text-xs text-slate-400 leading-relaxed">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="mt-8 pt-7 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-400">{t("footer.copyright")}</p>
          <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase">{t("footer.place")}</p>
        </div>
      </div>
    </footer>
  );
};
